import { getItem, setItem } from '../storage/interface';
import { docketsUpdated } from '../state/apolloVars';
import { Docket, DocketStatus } from '@oolio-group/domain';
import {
  DOCKETS,
  DocketPlacement,
  SortingPreference,
} from '../common/constants';
import { DocketSource, DocketUpdate } from '../common/types';
import { detectIncomingDocketThroughPolling } from '../utils/detectIncomingDocket';
import keyBy from 'lodash/keyBy';
import { groupBy } from 'lodash';
export const getDockets = async (status: DocketStatus) => {
  return ((await getItem(DOCKETS + `#${status}`)) as Docket[]) || [];
};

export const getAllDockets = async (): Promise<Docket[]> => {
  try {
    const dockets = await Promise.all([
      getDockets(DocketStatus.CREATED),
      getDockets(DocketStatus.COMPLETED),
    ]);
    return dockets.flat();
  } catch (e) {
    return [];
  }
};

export const clearExistingDockets = async (status: DocketStatus) => {
  await setItem(DOCKETS + `#${status}`, []);
  docketsUpdated({ timestamp: Date.now() });
};

export const saveDockets = async (
  docketItemId: string,
  updatedDockets: Docket[],
  source: DocketSource,
) => {
  if (updatedDockets?.length) {
    const docketsBystatus = groupBy(updatedDockets, 'status');
    Object.values(docketsBystatus).forEach(async dockets => {
      const status = dockets[0].status;
      const dbDockets =
        ((await getItem(DOCKETS + `#${status}`)) as Docket[]) || [];
      const dbDocketsMap = keyBy(dbDockets, 'id');
      dockets.forEach(docket => {
        if (dbDocketsMap[docket.id]?.id) {
          const changedItem = (docket?.docketItems || []).find(
            x => x.orderItemId == docketItemId,
          );
          if (docketItemId) {
            const updatedDocket = { ...dbDocketsMap[docket.id] };
            updatedDocket.docketItems = (updatedDocket.docketItems || []).map(
              x => {
                if (x.id == changedItem?.id) {
                  return changedItem;
                }
                return x;
              },
            );
            updatedDocket.status = docket.status;
            updatedDocket.itemStatus = docket.itemStatus;
            dbDocketsMap[docket.id] = updatedDocket;
          }
        }
      });
      await setItem(DOCKETS + `#${status}`, Object.values(dbDocketsMap));
    });
    docketsUpdated({ timestamp: Date.now(), source });
  }
};
export const swapUpdatedDockets = async (
  updatedDockets: Docket[],
  source: DocketSource,
) => {
  if (updatedDockets?.length) {
    const docketsBystatus = groupBy(updatedDockets, 'status');
    Object.values(docketsBystatus).forEach(async dockets => {
      const status = dockets[0].status;
      const dbDockets =
        ((await getItem(DOCKETS + `#${status}`)) as Docket[]) || [];
      const dbDocketsMap = keyBy(dbDockets, 'id');
      dockets.forEach(docket => {
        if (dbDocketsMap[docket.id]?.id) {
          dbDocketsMap[docket.id] = docket;
        }
      });
      await setItem(DOCKETS + `#${status}`, Object.values(dbDocketsMap));
    });
    docketsUpdated({ timestamp: Date.now(), source });
  }
};

type PartialDocket = {
  id: string;
  offset: number;
  status: Docket['status'];
};

export const updateDocketOffset = async (
  updatedDockets: PartialDocket[],
  source: DocketSource,
): Promise<void> => {
  if (!updatedDockets?.length) return;

  const docketsByStatus = groupBy(updatedDockets, 'status');

  await Promise.all(
    Object.entries(docketsByStatus).map(async ([status, dockets]) => {
      const key = DOCKETS + `#${status}`;
      const dbDockets = ((await getItem(key)) as Docket[]) || [];
      const dbDocketsMap = keyBy(dbDockets, 'id');

      for (const { id, offset } of dockets) {
        if (dbDocketsMap[id]) {
          dbDocketsMap[id].offset = offset;
        }
      }

      await setItem(key, Object.values(dbDocketsMap));
    }),
  );

  docketsUpdated({ timestamp: Date.now(), source });
};

export const saveDocketsOverwrite = async (
  dockets: Docket[],
  status: DocketStatus,
  source?: DocketSource,
) => {
  const docketUpdateVal: DocketUpdate = {
    timestamp: Date.now(),
    source: source,
  };

  if (dockets.length == 0) {
    await clearExistingDockets(status);
    return;
  }
  if (source == DocketSource.POOLING && status == DocketStatus.CREATED) {
    const existingDockets =
      ((await getItem(DOCKETS + `#${status}`)) as Docket[]) || [];
    if (detectIncomingDocketThroughPolling(dockets, existingDockets)) {
      docketUpdateVal['triggerSound'] = true;
    }
  }
  await setItem(DOCKETS + `#${status}`, dockets);
  docketsUpdated(docketUpdateVal);
  return dockets;
};

export const saveBumpedDocket = async (docketToBump: Docket) => {
  const docketId = docketToBump?.id;
  if (!docketId) return;
  let docketsToSaveInCurrent: Docket[] = [];
  let docketsToSaveInCompleted: Docket[] = [];
  docketsToSaveInCurrent =
    ((await getItem(DOCKETS + `#${DocketStatus.CREATED}`)) as Docket[]) || [];
  docketsToSaveInCompleted =
    ((await getItem(DOCKETS + `#${DocketStatus.COMPLETED}`)) as Docket[]) || [];
  const docket = docketsToSaveInCurrent.find(
    (dckt: Docket) => dckt.id == docketId,
  );
  docketsToSaveInCurrent = docketsToSaveInCurrent.filter(
    (dckt: Docket) => dckt.id !== docketId,
  );
  if (docket) {
    docketsToSaveInCompleted.push(docketToBump);
    await setItem(DOCKETS + `#${DocketStatus.CREATED}`, docketsToSaveInCurrent);
    await setItem(
      DOCKETS + `#${DocketStatus.COMPLETED}`,
      docketsToSaveInCompleted,
    );
    docketsUpdated({ timestamp: Date.now() });
  }
  return docketsToSaveInCurrent;
};

export const saveRecalledDocket = async (docketToRecall: Docket) => {
  const docketId = docketToRecall?.id;
  if (!docketId) return;
  let docketsToSaveInCurrent: Docket[] = [];
  let docketsToSaveInCompleted: Docket[] = [];
  docketsToSaveInCurrent =
    ((await getItem(DOCKETS + `#${DocketStatus.CREATED}`)) as Docket[]) || [];
  docketsToSaveInCompleted =
    ((await getItem(DOCKETS + `#${DocketStatus.COMPLETED}`)) as Docket[]) || [];
  const docket = docketsToSaveInCompleted.find(
    (dckt: Docket) => dckt.id == docketId,
  );
  docketsToSaveInCompleted = docketsToSaveInCompleted.filter(
    (dckt: Docket) => dckt.id !== docketId,
  );
  if (docket) {
    docketsToSaveInCurrent.push(docketToRecall);
    await setItem(DOCKETS + `#${DocketStatus.CREATED}`, docketsToSaveInCurrent);
    await setItem(
      DOCKETS + `#${DocketStatus.COMPLETED}`,
      docketsToSaveInCompleted,
    );
    docketsUpdated({ timestamp: Date.now() });
  } else {
    const docketIndex = docketsToSaveInCurrent.findIndex(
      docket => docket.id === docketId,
    );
    if (docketIndex >= 0) docketsToSaveInCurrent[docketIndex] = docketToRecall;
  }
  return docketsToSaveInCurrent;
};

export const sortDockets = (
  dockets: Docket[],
  preference: SortingPreference = SortingPreference.OLDEST_FIRST,
): Docket[] => {
  return [...dockets].sort((a, b) => {
    const aVirtual = a.createdAt + (a.offset ?? 0);
    const bVirtual = b.createdAt + (b.offset ?? 0);

    return preference === SortingPreference.NEWEST_FIRST
      ? bVirtual - aVirtual
      : aVirtual - bVirtual;
  });
};

export interface OffsetInput {
  target: Docket;
  before?: Docket | null;
  after?: Docket | null;
  position: DocketPlacement;
  sortingPreference: SortingPreference;
}
const MIN_DELTA = 1;

export const computeOffsetForTarget = ({
  target,
  before,
  after,
  position,
  sortingPreference,
}: OffsetInput): number => {
  const targetBaseTs = target.createdAt;
  const getTs = (d: Docket) => d.createdAt + (d.offset ?? 0);
  const isOldestFirst = sortingPreference === SortingPreference.OLDEST_FIRST;

  const getSafeDelta = (lowTs: number, highTs: number): number => {
    const gap = highTs - lowTs;
    return Math.max(Math.floor(gap / 2), MIN_DELTA);
  };

  const getMidpointOffset = (low: Docket, high: Docket): number => {
    const lowTs = getTs(low);
    const highTs = getTs(high);
    const midTs = (lowTs + highTs) / 2;
    return midTs - targetBaseTs;
  };

  switch (position) {
    case DocketPlacement.FORWARD: {
      const prev = isOldestFirst ? before : after;
      const next = isOldestFirst ? after : before;

      if (prev && next) {
        return getMidpointOffset(prev, next);
      } else if (next) {
        const nextTs = getTs(next);
        return nextTs - getSafeDelta(targetBaseTs, nextTs) - targetBaseTs;
      } else if (prev) {
        const prevTs = getTs(prev);
        return prevTs + getSafeDelta(prevTs, targetBaseTs) - targetBaseTs;
      }
      break;
    }

    case DocketPlacement.BACKWARD: {
      const prev = isOldestFirst ? before : after;
      const next = isOldestFirst ? after : before;

      if (prev && next) {
        return getMidpointOffset(prev, next);
      } else if (prev) {
        const prevTs = getTs(prev);
        return prevTs + getSafeDelta(prevTs, targetBaseTs) - targetBaseTs;
      } else if (next) {
        const nextTs = getTs(next);
        return nextTs - getSafeDelta(targetBaseTs, nextTs) - targetBaseTs;
      }
      break;
    }

    case DocketPlacement.START: {
      if (after) {
        const afterTs = getTs(after);
        const delta = getSafeDelta(targetBaseTs, afterTs);
        return isOldestFirst
          ? afterTs - delta - targetBaseTs
          : afterTs + delta - targetBaseTs;
      }
      break;
    }

    case DocketPlacement.END: {
      if (before) {
        const beforeTs = getTs(before);
        const delta = getSafeDelta(beforeTs, targetBaseTs);
        return isOldestFirst
          ? beforeTs + delta - targetBaseTs
          : beforeTs - delta - targetBaseTs;
      }
      break;
    }

    default: {
      if (before && after) {
        return getMidpointOffset(before, after);
      }
    }
  }

  return target.offset ?? 0;
};

export const repositionDocket = async (
  dockets: Docket[],
  targetDocketId: string,
  position: DocketPlacement,
  sortingPreference: SortingPreference = SortingPreference.OLDEST_FIRST,
): Promise<number> => {
  const target = dockets.find(x => x.id === targetDocketId);

  if (!target) throw new Error('Target docket not found');
  const storageKey = `${DOCKETS}#${target.status}`;

  const isRushed = !!target.rushedAt;

  // Filter dockets based on rushed status of target
  const scopedDockets = dockets.filter(d => !!d.rushedAt === isRushed);

  const sorted = sortDockets(scopedDockets, sortingPreference);

  const filtered = sorted.filter(d => d.id !== targetDocketId);
  const currentIndex = sorted.findIndex(d => d.id === targetDocketId);

  let newIndex: number;
  switch (position) {
    case DocketPlacement.START:
      newIndex = 0;
      break;
    case DocketPlacement.END:
      newIndex = filtered.length;
      break;
    case DocketPlacement.FORWARD:
      newIndex = Math.min(filtered.length, currentIndex + 1);
      break;
    case DocketPlacement.BACKWARD:
      newIndex = Math.max(0, currentIndex - 1);
      break;
    default:
      newIndex = filtered.length;
  }

  let before = null;
  let after = null;

  if (position === DocketPlacement.START) {
    after = filtered[0] ?? null;
  } else if (position === DocketPlacement.END) {
    before = filtered[filtered.length - 1] ?? null;
  } else {
    before = filtered[newIndex - 1] ?? null;
    after = filtered[newIndex] ?? null;
  }

  const newOffset = computeOffsetForTarget({
    target,
    before,
    after,
    position,
    sortingPreference,
  });

  // Apply new offset to target
  target.offset = newOffset;

  // Update and store the modified docket list
  const updatedDockets = dockets.map(d =>
    d.id === target.id ? { ...d, offset: newOffset } : d,
  );

  await setItem(storageKey, updatedDockets);
  docketsUpdated({ timestamp: Date.now() });

  return newOffset;
};
